import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';
import {
  statesNeedingProviderAttention as purchaseProviderStates,
  statesNeedingCustomerAttention as purchaseCustomerStates,
} from '../transactions/transactionProcessPurchase';

import { authInfo } from './auth.duck';
import { updateStripeConnectAccount } from './stripeConnectAccount.duck';
import { loadCartFromLocalStorage, setCart } from './cart.duck';
import { patchTransactionMetadata } from './marketplaceData.duck';

export const UPDATE_CURRENT_USER_PROFILE_REQUEST = 'app/user/UPDATE_CURRENT_USER_PROFILE_REQUEST';
export const UPDATE_CURRENT_USER_PROFILE_SUCCESS = 'app/user/UPDATE_CURRENT_USER_PROFILE_SUCCESS';
export const UPDATE_CURRENT_USER_PROFILE_ERROR = 'app/user/UPDATE_CURRENT_USER_PROFILE_ERROR';

// ================ Helper Functions ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
      ? newCurrentUser
      : { id, type, attributes, ...oldRelationships, ...relationships };
};

// ================ Async Thunks ================ //

//////////////////////////////////////////////////////////////////////
// Fetch ownListings to check if currentUser has published listings //
//////////////////////////////////////////////////////////////////////

const fetchCurrentUserHasListingsPayloadCreator = (_, thunkAPI) => {
  const { getState, extra: sdk, rejectWithValue } = thunkAPI;
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.resolve({ hasListings: false });
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    states: 'published',
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      return { hasListings: !!hasPublishedListings };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasListingsThunk = createAsyncThunk(
  'user/fetchCurrentUserHasListings',
  fetchCurrentUserHasListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasListingsThunk()).unwrap();
};

///////////////////////////////////////////////////////////
// Fetch transactions to check if currentUser has orders //
///////////////////////////////////////////////////////////

const fetchCurrentUserHasOrdersPayloadCreator = (_, { getState, extra: sdk, rejectWithValue }) => {
  if (!getState().user.currentUser) {
    return Promise.resolve({ hasOrders: false });
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      return { hasOrders: !!hasOrders };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasOrdersThunk = createAsyncThunk(
  'user/fetchCurrentUserHasOrders',
  fetchCurrentUserHasOrdersPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasOrdersThunk()).unwrap();
};

/////////////////////////////////////////////////////////////////////////////////////
// Fetch transactions in specific states to check if currentUser has notifications //
/////////////////////////////////////////////////////////////////////////////////////

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

// Active states where message notifications can occur
const ACTIVE_STATES = [
  'inquiry',
  'pending-payment',
  'purchased',
  'delivered',
  'disputed',
  'pending-partial-refund',
  'received',
  'completed',
  'reviewed-by-customer',
  'reviewed-by-provider',
];

const hasUnseenMessage = (tx, role) => {
  const metadata = tx.attributes?.metadata || {};
  const { lastMessageAt, lastMessageBy, messagesSeenAt } = metadata;
  if (!lastMessageAt || !lastMessageBy) return false;
  if (lastMessageBy === role) return false;
  const seenAt = messagesSeenAt?.[role];
  // If neither party has a messagesSeenAt entry, this is pre-feature data — treat as seen
  if (!seenAt && !messagesSeenAt?.[lastMessageBy]) return false;
  if (!seenAt) return true;
  return new Date(lastMessageAt) > new Date(seenAt);
};

const hasSeenCurrentState = (tx, role) => {
  const lastTransition = tx?.attributes?.lastTransition;
  const seenBy = tx?.attributes?.metadata?.seenBy || {};
  const seenTransitions = seenBy[role] || [];
  return lastTransition ? seenTransitions.includes(lastTransition) : false;
};

const fetchAllPages = (sdk, queryParams, maxPages = 10) => {
  const perPage = queryParams.perPage || NOTIFICATION_PAGE_SIZE;
  const fetchPage = page => sdk.transactions.query({ ...queryParams, page, perPage });

  return fetchPage(1).then(firstRes => {
    const firstData = firstRes?.data?.data || [];
    const meta = firstRes?.data?.meta || {};
    const totalPages = Math.min(meta.totalPages || 1, maxPages);

    if (totalPages <= 1) {
      return { data: firstData, meta };
    }

    const restPromises = Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2));
    return Promise.all(restPromises).then(restRes => {
      const restData = restRes.flatMap(r => r?.data?.data || []);
      return { data: [...firstData, ...restData], meta };
    });
  });
};

const fetchCurrentUserNotificationsPayloadCreator = (_, { extra: sdk, rejectWithValue, dispatch }) => {
  const statesNeedingProviderAttention = purchaseProviderStates || [];
  const statesNeedingCustomerAttention = purchaseCustomerStates || [];

  const activeStatesParam = ACTIVE_STATES.map(s => `state/${s}`).join(',');

  const paramsForSales = {
    only: 'sale',
    states: statesNeedingProviderAttention.map(state => `state/${state}`).join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
    processNames: 'default-purchase',
    'meta_isBidTransaction': false,
    'fields.transaction': ['lastTransition', 'metadata'],
  };
  const paramsForOrders = {
    only: 'order',
    states: statesNeedingCustomerAttention.map(state => `state/${state}`).join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
    processNames: 'default-purchase',
    'meta_isBidTransaction': false,
    'fields.transaction': ['lastTransition', 'metadata'],
  };
  // Query all active transactions to check for unseen messages
  const paramsForSaleMessages = {
    only: 'sale',
    states: activeStatesParam,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
    processNames: 'default-purchase',
    'fields.transaction': ['lastTransition', 'metadata'],
  };
  const paramsForOrderMessages = {
    only: 'order',
    states: activeStatesParam,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
    processNames: 'default-purchase',
    'fields.transaction': ['lastTransition', 'metadata'],
  };

  // Bid notifications:
  // - Provider: notify for new bid inquiries on their listings
  // - Customer: notify when their bid is accepted or rejected
  // Count only transitions that the role has not "seen" yet (seenBy).
  const paramsForCustomerBidNotifications = {
    only: 'order',
    processNames: 'default-purchase',
    lastTransitions: [
      'transition/accept-bid',
      'transition/reject-bid',
      'transition/operator-reject-bid',
    ].join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
    include: [],
    'meta_isBidTransaction': true,
    'fields.transaction': ['lastTransition', 'metadata'],
  };
  const paramsForProviderBidNotifications = {
    only: 'sale',
    processNames: 'default-purchase',
    lastTransitions: ['transition/inquire', 'transition/update-bid-by-customer'].join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
    include: [],
     'meta_isBidTransaction': true,
    'fields.transaction': ['lastTransition', 'metadata'],
  };

  // Explicit allowlist of transitions that warrant a notification dot per role.
  // This prevents expiry/auto transitions from generating false notifications.
  const SALE_NOTIFICATION_TRANSITIONS = [
    'transition/confirm-payment',
    'transition/confirm-push-payment',
    'transition/review-1-by-customer',
    'transition/review-2-by-customer',
  ];
  const ORDER_NOTIFICATION_TRANSITIONS = [
    'transition/review-1-by-provider',
    'transition/review-2-by-provider',
  ];
  // review-2 transitions only apply to transactions that went through the seenBy
  // tracking system. Legacy transactions in REVIEWED state have no seenBy data,
  // so we skip them to avoid false notifications.
  const REVIEW_2_TRANSITIONS = [
    'transition/review-2-by-customer',
    'transition/review-2-by-provider',
  ];

  return Promise.all([
    sdk.transactions.query(paramsForSales),
    sdk.transactions.query(paramsForOrders),
    sdk.transactions.query(paramsForSaleMessages),
    sdk.transactions.query(paramsForOrderMessages),
    fetchAllPages(sdk, paramsForCustomerBidNotifications),
    fetchAllPages(sdk, paramsForProviderBidNotifications),
  ])
    .then(([sales, orders, saleMessages, orderMessages, customerBidsNotifications, providerBidsNotifications]) => {
      // Merge state-based and message-based sale transactions, deduplicate by id
      const allSales = new Map();
      [...sales.data.data, ...saleMessages.data.data].forEach(tx => {
        if (!allSales.has(tx.id?.uuid)) allSales.set(tx.id?.uuid, tx);
      });
      const allOrders = new Map();
      [...orders.data.data, ...orderMessages.data.data].forEach(tx => {
        if (!allOrders.has(tx.id?.uuid)) allOrders.set(tx.id?.uuid, tx);
      });

      // Count each unique transaction once if it has a state dot OR a message dot
      const saleNotificationsCount = [...allSales.values()].filter(tx => {
        const lastTransition = tx.attributes?.lastTransition;
        const seenBy = tx.attributes?.metadata?.seenBy || {};
        const isBidTransaction = tx.attributes?.metadata?.isBidTransaction;
        // Bids have separate notification handling, so skip them in the sale count
        if (isBidTransaction) return false;
        const hasStateDot =
          SALE_NOTIFICATION_TRANSITIONS.includes(lastTransition) &&
          !(REVIEW_2_TRANSITIONS.includes(lastTransition) && Object.keys(seenBy).length === 0) &&
          !(seenBy['provider'] || []).includes(lastTransition);
        return hasStateDot || hasUnseenMessage(tx, 'provider');
      }).length;

      const orderNotificationsCount = [...allOrders.values()].filter(tx => {
        const lastTransition = tx.attributes?.lastTransition;
        const seenBy = tx.attributes?.metadata?.seenBy || {};
        const hasStateDot =
          ORDER_NOTIFICATION_TRANSITIONS.includes(lastTransition) &&
          !(REVIEW_2_TRANSITIONS.includes(lastTransition) && Object.keys(seenBy).length === 0) &&
          !(seenBy['customer'] || []).includes(lastTransition);
        return hasStateDot || hasUnseenMessage(tx, 'customer');
      }).length;

      // Patch Redux entities so inbox rows reflect fresh metadata without a full reload
      const allTxs = [...saleMessages.data.data, ...orderMessages.data.data];
      const unique = Array.from(new Map(allTxs.map(tx => [tx.id?.uuid, tx])).values());
      dispatch(patchTransactionMetadata(unique.map(tx => ({ id: tx.id?.uuid, metadata: tx.attributes?.metadata || {} }))));

      const customerBidTransitions = new Set([
        'transition/accept-bid',
        'transition/reject-bid',
        'transition/operator-reject-bid',
      ]);

      const customerBidsCount = (customerBidsNotifications?.data || []).filter(tx => {
        const lastTransition = tx?.attributes?.lastTransition;
        const isBidTransaction = tx?.attributes?.metadata?.isBidTransaction;

        return (
          isBidTransaction &&
          customerBidTransitions.has(lastTransition) &&
          !hasSeenCurrentState(tx, 'customer')
        );
      }).length;

      const providerBidTransitions = new Set([
        'transition/inquire',
        'transition/update-bid-by-customer',
      ]);

      const providerBidsCount = (providerBidsNotifications?.data || []).filter(tx => {
        const lastTransition = tx?.attributes?.lastTransition;
        const isBidTransaction = tx?.attributes?.metadata?.isBidTransaction;

        return (
          isBidTransaction &&
          providerBidTransitions.has(lastTransition) &&
          !hasSeenCurrentState(tx, 'provider')
        );
      }).length;

      const bidNotificationsCount = customerBidsCount + providerBidsCount;
      return {
        saleNotificationsCount,
        orderNotificationsCount,
        bidNotificationsCount,
        // Separate bid notification counts so we can deep-link the Inbox "Bids" view.
        myBidNotificationsCount: customerBidsCount,
        listingBidNotificationsCount: providerBidsCount,
      };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserNotificationsThunk = createAsyncThunk(
  'user/fetchCurrentUserNotifications',
  fetchCurrentUserNotificationsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserNotificationsThunk()).unwrap();
};

const fetchCurrentUserPayloadCreator = (options, thunkAPI) => {
  const { getState, dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve(state.user.currentUser);
  }

  if (!isAuthenticated && typeof window !== 'undefined') {
    // Cart loading is now handled by the cart duck
    dispatch(loadCartFromLocalStorage());
  }

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    return Promise.resolve(null);
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(updateStripeConnectAccount(currentUser.stripeAccount));
      }

      // Handle cart merge when user logs in (including IDP login from server redirect)
      if (afterLogin && typeof window !== 'undefined') {
        const cartFromLocalStorage = localStorage.getItem('cart');
        const localCart = cartFromLocalStorage ? JSON.parse(cartFromLocalStorage) : {};
        const serverCart = currentUser.attributes.profile?.privateData?.cart || {};
        const currentUserId = currentUser.id.uuid;

        // Only merge if there's a localStorage cart
        if (Object.keys(localCart).length > 0) {
          // Merge localStorage cart with server cart
          const mergedCart = { ...localCart };

          // Merge server cart into local cart (server cart takes precedence for duplicates)
          Object.keys(serverCart).forEach(providerId => {
            if (!mergedCart[providerId]) {
              mergedCart[providerId] = serverCart[providerId];
            } else {
              // Merge listings from the same provider
              mergedCart[providerId] = {
                ...mergedCart[providerId],
                ...serverCart[providerId],
              };
            }
          });

          // Remove user's own listings from cart (can't buy your own items)
          if (mergedCart[currentUserId]) {
            delete mergedCart[currentUserId];
          }

          // Update cart in Redux and prepare to save to server
          dispatch(setCart({ ...mergedCart }));

          // Save merged cart to server (async, don't block)
          dispatch(
            updateCurrentUserProfileThunk({
              data: {
                privateData: { cart: mergedCart },
              },
              options: {
                expand: true,
              },
            })
          ).then(() => {
            // Clear localStorage after successful save
            localStorage.removeItem('cart');
          });
        } else if (serverCart && Object.keys(serverCart).length > 0) {
          // No local cart, just load server cart
          dispatch(setCart({ ...serverCart }));
        }
      } else if (currentUser.attributes.profile?.privateData?.cart) {
        // Normal case: just load server cart
        dispatch(setCart({ ...currentUser.attributes.profile.privateData.cart }));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      return rejectWithValue(storableError(e));
    });
};

export const fetchCurrentUserThunk = createAsyncThunk(
  'user/fetchCurrentUser',
  fetchCurrentUserPayloadCreator
);
// Backward compatible wrapper for the thunk
/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserThunk(options)).unwrap();
};

/////////////////////////////////////////////
// Send verification email to currentUser //
/////////////////////////////////////////////

const sendVerificationEmailPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => ({}))
    .catch(e => rejectWithValue(storableError(e)));
};
export const sendVerificationEmailThunk = createAsyncThunk(
  'user/sendVerificationEmail',
  sendVerificationEmailPayloadCreator,
  {
    condition: (_, { getState }) => {
      return !getState()?.user?.sendVerificationEmailInProgress;
    },
  }
);

export const updateCurrentUserProfilePayloadCreator = (
  data,
  { dispatch, extra: sdk, rejectWithValue }
) => {
  return sdk.currentUser
    .updateProfile(data.data, data.options || { expand: true })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      dispatch(setCurrentUser(entities[0]));
      return response;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const updateCurrentUserProfileThunk = createAsyncThunk(
  'user/updateCurrentUserProfile',
  updateCurrentUserProfilePayloadCreator
);

// Backward compatible wrapper for the thunk
export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  return dispatch(sendVerificationEmailThunk()).unwrap();
};



// ================ Slice ================ //

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUser: null,
    currentUserShowTimestamp: 0,
    currentUserShowError: null,
    currentUserHasListings: false,
    currentUserHasListingsError: null,
    currentUserSaleNotificationCount: 0,
    currentUserOrderNotificationCount: 0,
    currentUserBidNotificationCount: 0,
    currentUserMyBidNotificationCount: 0,
    currentUserListingBidNotificationCount: 0,
    currentUserNotificationCountError: null,
    currentUserHasOrders: null, // This is not fetched unless unverified emails exist
    currentUserHasOrdersError: null,
    sendVerificationEmailInProgress: false,
    sendVerificationEmailError: null,

    updateCurrentUserProfileInProgress: false,
    updateCurrentUserProfileError: null,
    updateCurrentUserProfileSuccess: false,
  },
  reducers: {
    clearCurrentUser: state => {
      state.currentUser = null;
      state.currentUserShowError = null;
      state.currentUserHasListings = false;
      state.currentUserHasListingsError = null;
      state.currentUserSaleNotificationCount = 0;
      state.currentUserOrderNotificationCount = 0;
      state.currentUserBidNotificationCount = 0;
      state.currentUserMyBidNotificationCount = 0;
      state.currentUserListingBidNotificationCount = 0;

      state.currentUserNotificationCountError = null;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
    },
    setCurrentUserHasOrders: state => {
      state.currentUserHasOrders = true;
    },
  },
  extraReducers: builder => {
    builder
      // fetchCurrentUser
      .addCase(fetchCurrentUserThunk.pending, state => {
        state.currentUserShowError = null;
      })
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
        state.currentUserShowTimestamp = action.payload ? new Date().getTime() : 0;
      })
      .addCase(fetchCurrentUserThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload);
        state.currentUserShowError = action.payload;
      })
      // fetchCurrentUserHasListings
      .addCase(fetchCurrentUserHasListingsThunk.pending, state => {
        state.currentUserHasListingsError = null;
      })
      .addCase(fetchCurrentUserHasListingsThunk.fulfilled, (state, action) => {
        state.currentUserHasListings = action.payload.hasListings;
      })
      .addCase(fetchCurrentUserHasListingsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasListingsError = action.payload;
      })
      // fetchCurrentUserNotifications
      .addCase(fetchCurrentUserNotificationsThunk.pending, state => {
        state.currentUserNotificationCountError = null;
      })
      .addCase(fetchCurrentUserNotificationsThunk.fulfilled, (state, action) => {
        state.currentUserSaleNotificationCount = action.payload.saleNotificationsCount;
        state.currentUserOrderNotificationCount = action.payload.orderNotificationsCount;
        state.currentUserBidNotificationCount = action.payload.bidNotificationsCount || 0;
        state.currentUserMyBidNotificationCount = action.payload.myBidNotificationsCount || 0;
        state.currentUserListingBidNotificationCount =
          action.payload.listingBidNotificationsCount || 0;
      })
      .addCase(fetchCurrentUserNotificationsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserNotificationCountError = action.payload;
      })
      // fetchCurrentUserHasOrders
      .addCase(fetchCurrentUserHasOrdersThunk.pending, state => {
        state.currentUserHasOrdersError = null;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.fulfilled, (state, action) => {
        state.currentUserHasOrders = action.payload.hasOrders;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasOrdersError = action.payload;
      })
      // sendVerificationEmail
      .addCase(sendVerificationEmailThunk.pending, state => {
        state.sendVerificationEmailInProgress = true;
        state.sendVerificationEmailError = null;
      })
      .addCase(sendVerificationEmailThunk.fulfilled, state => {
        state.sendVerificationEmailInProgress = false;
      })
      .addCase(sendVerificationEmailThunk.rejected, (state, action) => {
        state.sendVerificationEmailInProgress = false;
        state.sendVerificationEmailError = action.payload;
      })
      .addCase(updateCurrentUserProfileThunk.pending, state => {
        state.updateCurrentUserProfileInProgress = true;
        state.updateCurrentUserProfileError = null;
      })
      .addCase(updateCurrentUserProfileThunk.fulfilled, state => {
        state.updateCurrentUserProfileInProgress = false;
        state.updateCurrentUserProfileSuccess = true;
      })
      .addCase(updateCurrentUserProfileThunk.rejected, (state, action) => {
        state.updateCurrentUserProfileInProgress = false;
        state.updateCurrentUserProfileError = action.payload;
      });
  },
});

export default userSlice.reducer;

export const { clearCurrentUser, setCurrentUser, setCurrentUserHasOrders } = userSlice.actions;

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};
